import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save, Refresh, Sync } from '@mui/icons-material';
import { allegroApiService } from '../shared/services/allegroApi';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [quantityThresholds, setQuantityThresholds] = useState({
    lowStockThreshold: 10,
    outOfStockThreshold: 0,
    enableAutoStatus: true,
  });
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await allegroApiService.getSettings();
      if (settings.quantityThresholds) {
        setQuantityThresholds(settings.quantityThresholds);
      }
    } catch (err) {
      setError('Błąd podczas ładowania ustawień');
      console.error('Błąd ładowania ustawień:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await allegroApiService.updateSettings({ quantityThresholds });
      
      setSuccess('Ustawienia zostały zapisane pomyślnie');
    } catch (err) {
      setError('Błąd podczas zapisywania ustawień');
      console.error('Błąd zapisywania ustawień:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleThresholdChange = (field, value) => {
    setQuantityThresholds(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  const handleSyncInventory = async () => {
    try {
      setSyncLoading(true);
      setError(null);
      setSuccess(null);
      
      await allegroApiService.syncInventory();
      
      setSuccess('Synchronizacja została uruchomiona pomyślnie');
    } catch (err) {
      setError('Błąd podczas uruchamiania synchronizacji');
      console.error('Błąd synchronizacji:', err);
    } finally {
      setSyncLoading(false);
    }
  };

  const getStatusDescription = (quantity) => {
    if (quantity <= quantityThresholds.outOfStockThreshold) {
      return 'Brak w magazynie';
    } else if (quantity <= quantityThresholds.lowStockThreshold) {
      return 'Niski stan';
    } else {
      return 'Dostępny';
    }
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
        Ustawienia
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Ustawienia stanu magazynowego */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Ustawienia stanu magazynowego
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveSettings}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Zapisz ustawienia
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Definiuj progi ilościowe dla automatycznego określania statusu produktów.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Próg niskiego stanu"
                    type="number"
                    value={quantityThresholds.lowStockThreshold}
                    onChange={(e) => handleThresholdChange('lowStockThreshold', e.target.value)}
                    helperText="Ilość poniżej której produkt ma status 'Niski stan'"
                    inputProps={{ min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Próg braku w magazynie"
                    type="number"
                    value={quantityThresholds.outOfStockThreshold}
                    onChange={(e) => handleThresholdChange('outOfStockThreshold', e.target.value)}
                    helperText="Ilość poniżej której produkt ma status 'Brak w magazynie'"
                    inputProps={{ min: 0 }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    checked={quantityThresholds.enableAutoStatus}
                    onChange={(e) => setQuantityThresholds(prev => ({
                      ...prev,
                      enableAutoStatus: e.target.checked
                    }))}
                  />
                }
                label="Automatyczne określanie statusu na podstawie ilości"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Podgląd statusów */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Podgląd statusów:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                      Dostępny
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ilość: {quantityThresholds.lowStockThreshold + 1}+
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                      Niski stan
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ilość: {quantityThresholds.outOfStockThreshold + 1} - {quantityThresholds.lowStockThreshold}
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                      Brak w magazynie
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ilość: {quantityThresholds.outOfStockThreshold}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Ustawienia aplikacji */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Ustawienia aplikacji
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Konfiguracja głównych ustawień aplikacji.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Ustawienia synchronizacji */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Synchronizacja z Allegro
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Sync />}
                  onClick={handleSyncInventory}
                  disabled={syncLoading}
                  sx={{ borderRadius: 2 }}
                >
                  {syncLoading ? 'Synchronizuję...' : 'Uruchom synchronizację'}
                </Button>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ręczne uruchomienie synchronizacji stanu magazynowego z platformą Allegro.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                Synchronizacja zaktualizuje stan magazynowy wszystkich produktów na podstawie aktualnych danych z Allegro.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Ustawienia powiadomień */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Ustawienia powiadomień
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Konfiguracja powiadomień i alertów.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings; 